import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";

// Animations loaded by URL (`animationPath`), shared across instances so each
// JSON file is fetched once per page view.
const animationCache = new Map();

const fetchAnimation = (path) => {
  if (!animationCache.has(path)) {
    animationCache.set(
      path,
      fetch(path)
        .then((res) => {
          if (!res.ok) throw new Error(`Failed to load ${path}`);
          return res.json();
        })
        .catch((err) => {
          animationCache.delete(path);
          throw err;
        })
    );
  }
  return animationCache.get(path);
};

const LottieIcon = (props) => {
  const ref = useRef();
  const containerRef = useRef();
  const [LottieComponent, setLottieComponent] = useState(null);
  const [loadedAnimation, setLoadedAnimation] = useState(null);

  useEffect(() => {
    import("lottie-react").then((module) => {
      setLottieComponent(() => module.default);
    });
  }, []);

  // Load lottie-react only in the browser to avoid SSR "document is not defined" errors
  useEffect(() => {
    let isMounted = true;

    if (typeof window !== "undefined") {
      import("lottie-react")
        .then((mod) => {
          if (isMounted) {
            setLottieComponent(() => mod.default || mod);
          }
        })
        .catch(() => {
          // Silently ignore load errors; component will just not render
        });
    }

    return () => {
      isMounted = false;
    };
  }, []);

  // `animationPath` keeps big animation JSON out of the JS bundle: the file is
  // fetched from /static only once the element is about to scroll into view.
  useEffect(() => {
    const path = props.animationPath;
    if (!path) return undefined;
    let isMounted = true;
    let observer;

    const load = () => {
      fetchAnimation(path)
        .then((data) => {
          if (isMounted) setLoadedAnimation(data);
        })
        .catch(() => {
          // Silently ignore load errors; component will just not render
        });
    };

    if (typeof IntersectionObserver === "undefined" || !containerRef.current) {
      load();
    } else {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            observer.disconnect();
            load();
          }
        },
        { rootMargin: "400px 0px" }
      );
      observer.observe(containerRef.current);
    }

    return () => {
      isMounted = false;
      if (observer) observer.disconnect();
    };
  }, [props.animationPath]);

  const animationData = props.animationPath
    ? loadedAnimation
    : props.animationData;

  // Lottie renders a <div><svg>, where `alt` means nothing, so expose `alt`
  // to screen readers as an image label instead (decorative when omitted).
  const a11yProps = props.alt
    ? { role: "img", "aria-label": props.alt }
    : { "aria-hidden": "true" };

  useEffect(() => {
    if (!ref.current) return;

    if (props.play) {
      ref.current?.play();
    } else {
      ref.current?.pause();
    }
  }, [props.play]);

  if (!LottieComponent || !animationData) {
    return (
      <div
        ref={containerRef}
        style={props.style}
        className={props?.className}
        {...a11yProps}
      />
    );
  }

  return (
    <LottieComponent
      className={props?.className}
      lottieRef={ref}
      autoplay={props.autoplay}
      loop={props.loop}
      {...a11yProps}
      animationData={animationData}
      style={props.style}
      onMouseEnter={() => {
        if (props.hover) ref.current?.play();
      }}
      onMouseLeave={() => {
        if (props.hover) ref.current?.pause();
      }}
    />
  );
};

LottieIcon.propTypes = {
  autoplay: PropTypes.bool,
  loop: PropTypes.bool,
  style: PropTypes.object,
  animationData: PropTypes.object,
  animationPath: PropTypes.string,
  hover: PropTypes.bool,
  play: PropTypes.bool,
  className: PropTypes.string,
  alt: PropTypes.string,
};

export default LottieIcon;
